/**
 * What the workflows remember per case in this browser: the last outcome of
 * each workflow, the "For This Case Only" notes, and which thread is each
 * lane's CaseBlink Workflow thread. Storage failures are harmless.
 */
import type { ResearchTopic } from "./researchTopics";
import type {
  FormChoice,
  LetterChoice,
  WorkflowId,
  WorkflowLane,
  WorkflowRunRecord,
  WorkflowScope
} from "./types";

export const runsKey = (individualId: string) =>
  `caseIde.workflowRuns.v1:${individualId}`;
export const notesKey = (individualId: string) =>
  `caseIde.workflowNotes.v1:${individualId}`;
export const threadKey = (individualId: string, lane: WorkflowLane = "main") =>
  lane === "main"
    ? `caseIde.workflowThread.v1:${individualId}`
    : `caseIde.workflowThread.v1:${lane}:${individualId}`;

/** The run still in progress. A refresh reads it back; it is not only in memory. */
export const queueKey = (individualId: string) =>
  `caseIde.workflowQueue.v1:${individualId}`;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const readJson = (key: string): Record<string, unknown> => {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeJson = (key: string, value: Record<string, unknown>) => {
  try {
    if (Object.keys(value).length)
      window.localStorage.setItem(key, JSON.stringify(value));
    else window.localStorage.removeItem(key);
  } catch {
    // Not remembered; harmless.
  }
};

const RUN_STATUSES = new Set(["done", "failed", "skipped"]);

export const readRuns = (
  individualId: string
): Partial<Record<WorkflowId, WorkflowRunRecord>> => {
  const out: Partial<Record<WorkflowId, WorkflowRunRecord>> = {};
  for (const [id, value] of Object.entries(readJson(runsKey(individualId)))) {
    if (
      isRecord(value) &&
      typeof value.status === "string" &&
      RUN_STATUSES.has(value.status) &&
      typeof value.at === "string"
    )
      out[id as WorkflowId] = value as WorkflowRunRecord;
  }
  return out;
};

export const writeRuns = (
  individualId: string,
  runs: Partial<Record<WorkflowId, WorkflowRunRecord>>
) => writeJson(runsKey(individualId), runs);

export const readNotes = (
  individualId: string
): Partial<Record<WorkflowId, string>> => {
  const out: Partial<Record<WorkflowId, string>> = {};
  for (const [id, value] of Object.entries(readJson(notesKey(individualId))))
    if (typeof value === "string" && value.trim())
      out[id as WorkflowId] = value;
  return out;
};

export const writeNotes = (
  individualId: string,
  notes: Partial<Record<WorkflowId, string>>
) => writeJson(notesKey(individualId), notes);

export const readThreadId = (
  individualId: string,
  lane: WorkflowLane = "main"
): string | null => {
  try {
    return window.localStorage.getItem(threadKey(individualId, lane));
  } catch {
    return null;
  }
};

export const writeThreadId = (
  individualId: string,
  id: string,
  lane: WorkflowLane = "main"
) => {
  try {
    window.localStorage.setItem(threadKey(individualId, lane), id);
  } catch {
    // Found again by its title next time.
  }
};

const WORKFLOW_IDS = new Set<WorkflowId>([
  "organize",
  "insights",
  "inconsistencies",
  "retitle",
  "number",
  "exhibitList",
  "research",
  "letters",
  "forms",
  "packet"
]);

const ENDED = new Set(["done", "failed", "skipped", "blocked"]);

/** One step still to run, or one whose prompt is already on its thread. */
export type StoredQueueItem = {
  id: WorkflowId;
  scope: WorkflowScope;
  chained: boolean;
  skippable: boolean;
  letters?: LetterChoice[];
  forms?: FormChoice[];
  topics?: ResearchTopic[];
  /** `start` has begun. A refresh follows it instead of sending a second prompt. */
  started?: boolean;
  /** The prompt is on the thread. A refresh must not send it again. */
  sent?: boolean;
  threadId?: string;
};

/** Run Everything (or Run Remaining) that a refresh has to continue. */
export type StoredQueue = {
  items: StoredQueueItem[];
  ended: Partial<Record<WorkflowId, "done" | "failed" | "skipped" | "blocked">>;
  worked: WorkflowId[];
  order: WorkflowId[];
  researchGate: boolean;
};

const isId = (id: string): id is WorkflowId =>
  WORKFLOW_IDS.has(id as WorkflowId);

const isScope = (v: unknown): v is WorkflowScope => {
  if (!isRecord(v)) return false;
  if (v.kind === "default" || v.kind === "uploadNames") return true;
  return (
    v.kind === "selection" &&
    Array.isArray(v.nodeIds) &&
    v.nodeIds.every((id) => typeof id === "string")
  );
};

const isLetter = (v: unknown): v is LetterChoice =>
  isRecord(v) &&
  typeof v.draftType === "string" &&
  typeof v.label === "string" &&
  (v.template === null ||
    (isRecord(v.template) &&
      typeof v.template.entryId === "string" &&
      typeof v.template.name === "string"));

const isForm = (v: unknown): v is FormChoice =>
  isRecord(v) &&
  typeof v.formNumber === "string" &&
  typeof v.title === "string" &&
  typeof v.name === "string" &&
  (v.formId === null || typeof v.formId === "string") &&
  (v.label === null || typeof v.label === "string");

const isTopic = (v: unknown): v is ResearchTopic =>
  isRecord(v) && typeof v.id === "string" && typeof v.title === "string";

const itemOf = (v: unknown): StoredQueueItem | null => {
  if (!isRecord(v) || typeof v.id !== "string" || !isId(v.id)) return null;
  if (!isScope(v.scope)) return null;
  if (typeof v.chained !== "boolean" || typeof v.skippable !== "boolean")
    return null;
  const letters = Array.isArray(v.letters)
    ? v.letters.filter(isLetter)
    : undefined;
  const forms = Array.isArray(v.forms) ? v.forms.filter(isForm) : undefined;
  const topics = Array.isArray(v.topics) ? v.topics.filter(isTopic) : undefined;
  return {
    id: v.id,
    scope: v.scope,
    chained: v.chained,
    skippable: v.skippable,
    ...(letters?.length ? { letters } : {}),
    ...(forms?.length ? { forms } : {}),
    ...(topics?.length ? { topics } : {}),
    ...(v.started === true ? { started: true } : {}),
    ...(v.sent === true ? { sent: true } : {}),
    ...(typeof v.threadId === "string" && v.threadId
      ? { threadId: v.threadId }
      : {})
  };
};

export const readQueue = (individualId: string): StoredQueue | null => {
  try {
    const raw = window.localStorage.getItem(queueKey(individualId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!isRecord(parsed) || !Array.isArray(parsed.items)) return null;
    const items = parsed.items
      .map(itemOf)
      .filter((item): item is StoredQueueItem => item !== null);
    if (!items.length) return null;
    const ended: StoredQueue["ended"] = {};
    if (isRecord(parsed.ended)) {
      for (const [id, status] of Object.entries(parsed.ended))
        if (isId(id) && typeof status === "string" && ENDED.has(status))
          ended[id] = status as StoredQueue["ended"][WorkflowId];
    }
    const worked = Array.isArray(parsed.worked)
      ? parsed.worked.filter(
          (id): id is WorkflowId => typeof id === "string" && isId(id)
        )
      : [];
    const order = Array.isArray(parsed.order)
      ? parsed.order.filter(
          (id): id is WorkflowId => typeof id === "string" && isId(id)
        )
      : items.map((item) => item.id);
    return {
      items,
      ended,
      worked,
      order,
      researchGate: parsed.researchGate === true
    };
  } catch {
    return null;
  }
};

export const writeQueue = (individualId: string, queue: StoredQueue) => {
  try {
    if (!queue.items.length) {
      window.localStorage.removeItem(queueKey(individualId));
      return;
    }
    window.localStorage.setItem(queueKey(individualId), JSON.stringify(queue));
  } catch {
    // Not remembered; a refresh would drop the rest of the run.
  }
};

export const clearQueue = (individualId: string) => {
  try {
    window.localStorage.removeItem(queueKey(individualId));
  } catch {
    // Already gone.
  }
};
